/**
 * WhatsApp click-to-chat helpers.
 *
 * Uses the free `https://wa.me/<phone>?text=<text>` deep-link so no API keys
 * or paid Twilio setup is required. The user reviews the pre-drafted message
 * in their own WhatsApp and hits send.
 */

// KINOS company WhatsApp — used from the public quotation approval page so
// the client can message KINOS back. Mirrors `branding.COMPANY_PHONE` on the
// backend.
export const KINOS_WA_PHONE = "+670 77138966";

const MSG = {
  quotation: {
    en: ({ client, docNum, total, currency, validUntil, company, approveUrl }) => {
      let s =
        `Hi ${client}, following up on our quotation *${docNum}* — total ${total} ${currency}` +
        (validUntil ? ` (valid until ${validUntil})` : "") +
        `.`;
      if (approveUrl) {
        s += `\n\nApprove online in one click: ${approveUrl}`;
      } else {
        s += ` Please confirm if you'd like us to proceed.`;
      }
      return s + `\n\nThanks! — ${company}`;
    },
    tt: ({ client, docNum, total, currency, validUntil, company, approveUrl }) => {
      let s =
        `Bondia ${client}, ami hato'o kotasaun *${docNum}* ho valor ${total} ${currency}` +
        (validUntil ? ` (válidu to'o ${validUntil})` : "") +
        `.`;
      if (approveUrl) {
        s += `\n\nAprova online iha click ida: ${approveUrl}`;
      } else {
        s += ` Favór ida konfirma se ita bele kontinua ho ida-ne'e.`;
      }
      return s + `\n\nObrigadu. — ${company}`;
    },
  },
  invoice: {
    en: ({ client, docNum, total, currency, balance, dueDate, company }) => {
      const owed = balance != null && balance > 0 ? balance : total;
      return `Hi ${client}, following up on invoice *${docNum}* — outstanding balance ${owed} ${currency}` +
        (dueDate ? ` (due ${dueDate})` : "") +
        `. Please arrange payment or let us know if you have any questions. — ${company}`;
    },
    tt: ({ client, docNum, total, currency, balance, dueDate, company }) => {
      const owed = balance != null && balance > 0 ? balance : total;
      return `Bondia ${client}, ami fó lembransa kona-ba faktura *${docNum}* — saldu falta ${owed} ${currency}` +
        (dueDate ? ` (vencementu ${dueDate})` : "") +
        `. Favór ida halo pagamentu ka kontakta ami se iha dúvida. Obrigadu. — ${company}`;
    },
  },
  publicApproval: {
    en: ({ client, docNum, company }) =>
      `Hi ${company}, this is ${client}. I'm reviewing quotation *${docNum}* and have a question — could you help me? Thanks.`,
    tt: ({ client, docNum, company }) =>
      `Bondia ${company}, ha'u mak ${client}. Ha'u foin haree kotasaun *${docNum}* no iha pergunta ida — bele ajuda ha'u? Obrigadu.`,
  },
};

// Strip everything except digits and a leading `+`, then drop the `+` because
// wa.me expects just digits.
export function normalizeWhatsappPhone(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  // Keep only digits & leading +.
  let cleaned = trimmed.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+")) cleaned = cleaned.slice(1);
  return cleaned;
}

// Build a wa.me URL. When `phone` is empty, returns a bare wa.me link — WA
// will open with the message but no recipient, letting the user pick.
export function buildWaUrl(phone, message) {
  const p = normalizeWhatsappPhone(phone);
  const text = encodeURIComponent(message || "");
  return p ? `https://wa.me/${p}?text=${text}` : `https://wa.me/?text=${text}`;
}

/**
 * Open a WA chat drafted for a quotation.
 *
 * @param {object} q       - quotation doc (with client_snapshot & totals)
 * @param {string} locale  - "en" or "tt"
 * @param {string} company - company name to sign the message with
 */
export function openWaForQuotation(q, locale = "en", company = "KINOS", approveUrl = "") {
  const client = q.client_snapshot || {};
  const to = client.phone || client.email || "";
  const build = (MSG.quotation[locale] || MSG.quotation.en);
  const text = build({
    client: (client.contact_person || client.name || "there").split(" ")[0],
    docNum: q.quotation_number || q.id,
    total: (q.total ?? 0).toLocaleString(),
    currency: q.currency || "USD",
    validUntil: q.valid_until || "",
    approveUrl,
    company,
  });
  window.open(buildWaUrl(to, text), "_blank", "noopener,noreferrer");
}

export function openWaForInvoice(inv, locale = "en", company = "KINOS") {
  const client = inv.client_snapshot || {};
  const to = client.phone || client.email || "";
  const build = (MSG.invoice[locale] || MSG.invoice.en);
  const text = build({
    client: (client.contact_person || client.name || "there").split(" ")[0],
    docNum: inv.invoice_number || inv.id,
    total: (inv.total ?? 0).toLocaleString(),
    balance: inv.balance_due,
    currency: inv.currency || "USD",
    dueDate: inv.due_date || "",
    company,
  });
  window.open(buildWaUrl(to, text), "_blank", "noopener,noreferrer");
}

// Client → KINOS message from the public approval page.
export function openWaFromPublicApproval(q, locale = "en", company = "KINOS") {
  const client = q.client_snapshot || {};
  const build = (MSG.publicApproval[locale] || MSG.publicApproval.en);
  const text = build({
    client: client.contact_person || client.name || "a client",
    docNum: q.quotation_number || q.id,
    company,
  });
  window.open(buildWaUrl(KINOS_WA_PHONE, text), "_blank", "noopener,noreferrer");
}

// ─── Deposit / Partial payment receipt ───────────────────────────────────

const money = (n, ccy) => `${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} ${ccy}`;

const RECEIPT_MSG = {
  en: (r, company) => {
    const who = (r.client_contact || r.client_name || "there").split(" ")[0];
    const fullyPaid = !!r.is_fully_paid;
    const lines = [
      `Hi ${who}, thank you — we've received your ${fullyPaid ? "payment" : "deposit"} for invoice *${r.invoice_number}*.`,
      "",
      `• Amount received: ${money(r.amount_paid, r.currency)}`,
      `• Total paid: ${money(r.total_paid, r.currency)}`,
      fullyPaid
        ? `• Status: *Fully paid* ✅`
        : `• Remaining balance: ${money(r.remaining, r.currency)}` + (r.due_date ? ` (due ${r.due_date})` : ""),
    ];
    if (r.public_pdf_url) {
      lines.push("", `Receipt / invoice PDF: ${r.public_pdf_url}`);
    }
    lines.push("", `— ${company}`);
    return lines.join("\n");
  },
  tt: (r, company) => {
    const who = (r.client_contact || r.client_name || "kliente").split(" ")[0];
    const fullyPaid = !!r.is_fully_paid;
    const lines = [
      `Bondia ${who}, obrigadu — ami simu ita-nia ${fullyPaid ? "pagamentu" : "depozitu"} ba faktura *${r.invoice_number}*.`,
      "",
      `• Montante simu: ${money(r.amount_paid, r.currency)}`,
      `• Total pagu: ${money(r.total_paid, r.currency)}`,
      fullyPaid
        ? `• Status: *Selu tomak* ✅`
        : `• Saldu sei falta: ${money(r.remaining, r.currency)}` + (r.due_date ? ` (vencementu ${r.due_date})` : ""),
    ];
    if (r.public_pdf_url) {
      lines.push("", `Resibu / faktura PDF: ${r.public_pdf_url}`);
    }
    lines.push("", `— ${company}`);
    return lines.join("\n");
  },
};

/** Build the plain-text receipt message (used for WA drafts and copy-to-clipboard). */
export function buildReceiptMessage(receipt, locale = "en", company = "KINOS") {
  const build = RECEIPT_MSG[locale] || RECEIPT_MSG.en;
  return build(receipt || {}, company);
}

/** Open WhatsApp with a pre-drafted deposit / payment receipt. */
export function openWaForReceipt(receipt, locale = "en", company = "KINOS") {
  const text = buildReceiptMessage(receipt, locale, company);
  window.open(buildWaUrl(receipt?.client_phone, text), "_blank", "noopener,noreferrer");
}

// ─── Client → KINOS "I've paid" notification (from Client Portal) ─────────

const NOTIFY_PAYMENT_MSG = {
  en: ({ clientName, docNum, amount, currency, company }) =>
    `Hi ${company}, this is ${clientName || "your client"}. I've made a payment of ` +
    `*${amount} ${currency}* against invoice *${docNum}*. Please confirm receipt when convenient. Thank you.`,
  tt: ({ clientName, docNum, amount, currency, company }) =>
    `Bondia ${company}, ha'u mak ${clientName || "kliente"}. Ha'u foin selu ` +
    `*${amount} ${currency}* ba faktura *${docNum}*. Favór ida konfirma bainhira ita bele. Obrigadu.`,
};

export function buildNotifyPaymentMessage({ clientName, docNum, amount, currency }, locale = "en", company = "KINOS") {
  const build = NOTIFY_PAYMENT_MSG[locale] || NOTIFY_PAYMENT_MSG.en;
  return build({ clientName, docNum, amount, currency, company });
}

export function openWaNotifyPayment(payload, locale = "en", company = "KINOS") {
  const text = buildNotifyPaymentMessage(payload, locale, company);
  window.open(buildWaUrl(KINOS_WA_PHONE, text), "_blank", "noopener,noreferrer");
}
